import React, { useEffect, useState } from 'react';
import {
  Button, Table, Badge, Spinner,
} from 'react-bootstrap';
import _ from 'lodash';
import Error from '../../Components/Error';
import DiscountCodeInputModal from './Components/DiscountCodeInputModal';
import useModalState from '../../Hooks/useModalState';
import DiscountCodeService from '../../Services/DiscountCodeService';

function DiscountCodes() {
  const [discountCodes, setDiscountCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const inputModal = useModalState();

  const fetchDiscountCodes = async () => {
    setLoading(true);
    try {
      const response = await DiscountCodeService.fetchDiscountCodes();
      setDiscountCodes(response?.data?.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetch = _.debounce(() => {
    fetchDiscountCodes();
  }, 500);

  useEffect(() => {
    debouncedFetch();
    return () => debouncedFetch.cancel();
  }, []);

  const handleDelete = async (discountCode) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Are you sure you want to delete discount code "${discountCode.code}"?`)) {
      return;
    }

    try {
      await DiscountCodeService.deleteDiscountCode(discountCode.Id);
      fetchDiscountCodes();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete discount code');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const isExpired = (discountCode) => {
    if (!discountCode.valid_until) return false;
    return new Date(discountCode.valid_until) < new Date();
  };

  const isNotYetValid = (discountCode) => {
    if (!discountCode.valid_from) return false;
    return new Date(discountCode.valid_from) > new Date();
  };

  const getStatusBadge = (discountCode) => {
    if (discountCode.active !== 1) {
      return <Badge bg="secondary">Inactive</Badge>;
    }
    if (isExpired(discountCode)) {
      return <Badge bg="danger">Expired</Badge>;
    }
    if (isNotYetValid(discountCode)) {
      return <Badge bg="warning" text="dark">Scheduled</Badge>;
    }
    return <Badge bg="success">Active</Badge>;
  };

  const getUsageBadge = (discountCode) => {
    const current = discountCode.current_uses || 0;
    const max = discountCode.max_uses || 0;

    if (max === 0) {
      return (
        <Badge bg="light" text="dark">
          {current}
          {' '}
          / Unlimited
        </Badge>
      );
    }

    const isMaxed = current >= max;
    return (
      <Badge bg={isMaxed ? 'danger' : 'light'} text={isMaxed ? 'white' : 'dark'}>
        {current}
        {' '}
        /
        {' '}
        {max}
      </Badge>
    );
  };

  return (
    <>
      {/* Hero Section */}
      <div className="hero-section mb-4">
        <div className="row align-items-center">
          <div className="col-lg-8 mb-3 mb-lg-0">
            <h1 className="display-5 fw-bold mb-3">
              Discount
              {' '}
              <span className="hero-highlight">Codes</span>
            </h1>
            <p className="lead text-muted mb-4">
              Manage promotional discount codes for customer bookings
            </p>
            <div className="hero-actions d-flex gap-2">
              <Button
                onClick={() => inputModal.show({})}
                size="lg"
                variant="primary"
              >
                <i className="fa fa-plus me-2" />
                Create Discount Code
              </Button>
            </div>
          </div>
          <div className="col-lg-4 d-none d-lg-block text-end">
            <div className="hero-icon">
              <i className="fa fa-tags fa-5x text-primary opacity-25" />
            </div>
          </div>
        </div>
      </div>

      <Error error={error} setError={setError} />

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="text-muted mt-2">Loading discount codes...</p>
        </div>
      ) : (
        <Table responsive striped bordered hover>
          <thead>
            <tr>
              <th>Code</th>
              <th>Discount</th>
              <th>Validity Period</th>
              <th>Usage</th>
              <th>Status</th>
              <th style={{ width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {discountCodes.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  <i className="fa fa-tags fa-2x mb-2 d-block opacity-50" />
                  No discount codes found. Create your first discount code.
                </td>
              </tr>
            ) : (
              discountCodes.map((code) => (
                <tr key={code.Id}>
                  <td>
                    <strong className="font-monospace">{code.code}</strong>
                    {code.description && (
                      <small className="text-muted d-block text-truncate" style={{ maxWidth: '200px' }}>
                        {code.description}
                      </small>
                    )}
                  </td>
                  <td>
                    <Badge bg="primary" className="fs-6">
                      {code.discount_percentage}
                      %
                    </Badge>
                  </td>
                  <td>
                    <small>
                      {formatDate(code.valid_from)}
                      {' '}
                      -
                      {' '}
                      {formatDate(code.valid_until)}
                    </small>
                  </td>
                  <td>{getUsageBadge(code)}</td>
                  <td>{getStatusBadge(code)}</td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => inputModal.show(code)}
                      >
                        <i className="fa fa-edit me-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => handleDelete(code)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {inputModal.isVisible && (
        <DiscountCodeInputModal
          show={inputModal.isVisible}
          onHide={inputModal.hide}
          discountCode={inputModal.selectedObject}
          onSuccess={fetchDiscountCodes}
        />
      )}
    </>
  );
}

export default DiscountCodes;
